import { Button } from "@/components/ui/button";
import { formatDate } from "@/utils/format-date";
import { CheckCircle2 } from "lucide-react";
import { JSX } from "react";

import { useIdentityQueue, IdentityQueueState } from "../../hooks/use-identity-queue";
import { amountToneOf, formatAmount } from "../../utils/amount-display";
import { IdentityQueueEntry } from "../../utils/identity-queue";
import { IDENTITY_QUEUE_LABELS } from "../../utils/messages";
import { BatchItem } from "../shared/batch-action-modal";
import {
  DecisionConfirmationModal,
  DecisionSubject,
} from "./decision-confirmation-modal";
import { DecisionStatus } from "../../utils/decision-consequence";

export const IDENTITY_QUEUE_TEST_ID: string = "identity-queue";

export const IDENTITY_QUEUE_PROGRESS_TEST_ID: string = "identity-queue-progress";

export const IDENTITY_QUEUE_DONE_TEST_ID: string = "identity-queue-done";

interface IdentityQueueModalProps {
  entries: IdentityQueueEntry<BatchItem>[];
  action: DecisionStatus;
  onFinished: () => void;

  // Itens que o pre-flight barrou antes da fila. Sem o modal de sumario eles
  // nao teriam onde aparecer, e sumir em silencio e pior que a tela a mais.
  blockedCount?: number;
}

const CaseHeader = ({ item }: { item: BatchItem }): JSX.Element => (
  <div className="flex items-start justify-between gap-3 rounded-lg border bg-gray-50 p-3 text-sm">
    <div className="space-y-1">
      <div className="font-medium text-[#613F4F]">
        {IDENTITY_QUEUE_LABELS.CASE(item.id)}
      </div>
      <div className="space-y-0.5 text-xs text-gray-500">
        <div>
          {IDENTITY_QUEUE_LABELS.STORE}{" "}
          {item.store_code ?? item.conciliation?.store?.code ?? "-"}
        </div>
        <div>
          {IDENTITY_QUEUE_LABELS.DATE}{" "}
          {formatDate(item.conciliation?.date, { fallback: "-" })}
        </div>
      </div>
    </div>
    <div className={`font-semibold ${amountToneOf(item.amount)}`}>
      {formatAmount(item.amount)}
    </div>
  </div>
);

export function IdentityQueueModal({
  entries,
  action,
  onFinished,
  blockedCount = 0,
}: IdentityQueueModalProps): JSX.Element {
  const queue: IdentityQueueState<BatchItem> = useIdentityQueue(entries);

  if (queue.isDone || !queue.current) {
    return (
      <div
        className="mx-auto flex max-w-lg flex-col items-center gap-4"
        data-testid={IDENTITY_QUEUE_DONE_TEST_ID}
      >
        <CheckCircle2 className="h-10 w-10 text-green-600" aria-hidden="true" />
        <span className="font-medium text-[#613F4F]">
          {IDENTITY_QUEUE_LABELS.DONE_TITLE}
        </span>
        <p className="text-sm text-gray-700">
          {IDENTITY_QUEUE_LABELS.DONE_RESOLVED(queue.resolvedCount)}
        </p>
        {queue.skippedCount > 0 && (
          <p className="text-sm text-amber-700">
            {IDENTITY_QUEUE_LABELS.DONE_SKIPPED(queue.skippedCount)}
          </p>
        )}
        {blockedCount > 0 && (
          <p className="text-sm text-amber-700">
            {IDENTITY_QUEUE_LABELS.DONE_BLOCKED(blockedCount)}
          </p>
        )}
        <Button variant="outline" onClick={onFinished}>
          {IDENTITY_QUEUE_LABELS.CLOSE}
        </Button>
      </div>
    );
  }

  const entry: IdentityQueueEntry<BatchItem> = queue.current;

  return (
    <div
      className="mx-auto flex max-w-lg flex-col gap-4"
      data-testid={IDENTITY_QUEUE_TEST_ID}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-700">
          {IDENTITY_QUEUE_LABELS.INTRO(queue.total)}
        </p>
        <span
          className="shrink-0 rounded-full bg-[#F6D040] px-3 py-1 text-xs font-medium text-[#613F4F]"
          data-testid={IDENTITY_QUEUE_PROGRESS_TEST_ID}
        >
          {IDENTITY_QUEUE_LABELS.PROGRESS(queue.position, queue.total)}
        </span>
      </div>

      <CaseHeader item={entry.item} />

      <DecisionConfirmationModal
        key={entry.item.id}
        divergenceId={entry.item.id}
        divergence={entry.item as DecisionSubject}
        action={action}
        initialInstallments={entry.installmentsCount}
        queue={{
          position: queue.position,
          total: queue.total,
          onResolved: queue.resolve,
          onSkip: queue.skip,
        }}
      />
    </div>
  );
}
